import React, { useEffect, useState } from "react";
import SummaryApi from "../common";
import { useNavigate } from "react-router-dom";
import displayINRCurrency from "../helpers/displayCurrency";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { FaFileExcel } from "react-icons/fa6";
import scrollTop from "../helpers/scrollTop";

const StudentReports = () => {
  const [students, setStudents] = useState([]);
  const [form, setForm] = useState({
    student_id: "",
    student_admission_number: "",
    full_name: "",
    father_name: "",
    mobile_number: "",
    email_id: "",
    course: "",
    fee: "",
    payment_method: "",
    payment_date: "",
    other_charges: "",
    transaction_id: "",
    invoice_number: "",
    total_amount: "",
    remarks: "", // Added remarks to the initial form state
  });
  const [editingIndex, setEditingIndex] = useState(null);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const studentsPerPage = 10;
  const navigate = useNavigate();

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      const response = await fetch(SummaryApi.getAllStudents.url);
      const result = await response.json();

      if (result.status && Array.isArray(result.data)) {
        setStudents(result.data);
      } else {
        console.error("Unexpected response:", result);
      }
    } catch (error) {
      console.error("Error fetching students:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Convert to uppercase if the field is transaction_id or student_admission_number
    const uppercaseFields = [
      "full_name",
      "transaction_id",
      "student_admission_number",
    ];
    const updatedValue = uppercaseFields.includes(name)
      ? value.toUpperCase()
      : value;

    const updatedForm = {
      ...form,
      [name]: updatedValue,
    };

    // Recalculate total_amount if fee or other_charges change
    const fee = parseFloat(name === "fee" ? updatedValue : form.fee) || 0;
    const otherCharges =
      parseFloat(
        name === "other_charges" ? updatedValue : form.other_charges
      ) || 0;
    updatedForm.total_amount = fee + otherCharges;

    setForm(updatedForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Conditional validation
    if (form.payment_method !== "Cash" && form.transaction_id.trim() === "") {
      toast.error("Transaction ID is required for non-cash payments.");
      return;
    }

    const api =
      editingIndex !== null
        ? SummaryApi.updateStudent
        : SummaryApi.createStudent;

    try {
      const response = await fetch(api.url, {
        method: api.method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...form }),
      });

      const result = await response.json();
      if (result.status) {
        await fetchStudents(); // This will update the 'students' state
        toast.success("Student Form Submitted Successfully!");

        // Find the newly submitted student in the updated students list
        const submittedStudent = result.data; // Assuming the API returns the created/updated student data

        if (submittedStudent) {
          navigate("/invoice", {
            state: { student: submittedStudent },
          });
        } else {
          // Fallback if the specific student is not in the response
          const updatedStudents = await (
            await fetch(SummaryApi.getAllStudents.url)
          ).json();
          const newStudent = updatedStudents.data.find(
            (s) => s.student_admission_number === form.student_admission_number
          );
          if (newStudent) {
            navigate("/invoice", {
              state: { student: newStudent },
            });
          }
        }

        setForm({
          student_id: "",
          student_admission_number: "",
          full_name: "",
          father_name: "",
          mobile_number: "",
          email_id: "",
          course: "",
          year: "",
          semester: "",
          fee: "",
          payment_method: "",
          payment_date: "",
          other_charges: "",
          transaction_id: "",
          invoice_number: "",
          total_amount: "",
          remarks: "", // Reset remarks on form clear
        });
        setEditingIndex(null);
      } else {
        toast.error(result.message || "Error");
        console.error("API error:", result.message || result);
      }
    } catch (error) {
      toast.error("Submission error");
      console.error("Submission error:", error);
    }
  };

  const handleEdit = (index) => {
    
const student = filteredStudents[index];
    setForm({
      student_id: student.student_id,
      student_admission_number: student.student_admission_number,
      full_name: student.full_name,
      father_name: student.father_name,
      mobile_number: student.mobile_number,
      email_id: student.email_id,
      course: student.course,
      year: student.year,
      semester: student.semester,
      fee: student.fee,
      payment_method: student.payment_method || "",
      payment_date: student.payment_date || "",
      other_charges: student.other_charges || "",
      transaction_id: student.transaction_id || "",
      invoice_number: student.invoice_number || "",
      total_amount: student.total_amount || "",
      remarks: student.remarks || "", // Set remarks for editing
    });
    setEditingIndex(index);
    scrollTop();
  };

  const handleInvoice = (student) => {
    navigate("/invoice", { state: { student } });
    scrollTop();
  };

  const filteredStudents = students.filter((student) => {
    const searchValue = search.toLowerCase();
    return (
      student.student_admission_number?.toLowerCase().includes(searchValue) ||
      student.mobile_number?.includes(searchValue)
    );
  });

  const indexOfLastStudent = currentPage * studentsPerPage;
  const indexOfFirstStudent = indexOfLastStudent - studentsPerPage;
  const currentStudents = filteredStudents.slice(
    indexOfFirstStudent,
    indexOfLastStudent
  );

  const totalPages = Math.ceil(filteredStudents.length / studentsPerPage);

  const handleDownloadExcel = () => {
    const data = filteredStudents.map(
      (
        {
          student_admission_number,
          full_name,
          father_name,
          mobile_number,
          email_id,
          course,
          year,
          semester,
          fee,
          payment_method,
          payment_date,
          // other_charges,
          transaction_id,
          invoice_number,
          total_amount,
          remarks, // Added remarks to excel export
        },
        index
      ) => ({
        "S.No": index + 1,
        "Transaction ID": transaction_id,
        "Invoice Number": invoice_number,
        "Student Admission Number": student_admission_number,
        "Student Name": full_name,
        "Father Name": father_name,
        Mobile: mobile_number,
        Email: email_id,
        Course: course,
        Year: year,
        Semester: semester,
        Fee: fee,
        // "Other Charges": other_charges,
        "Total Amount": total_amount,
        "Payment Method": payment_method,
        "Payment Date": payment_date,
        Remarks: remarks, // Added remarks column header
      })
    );

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Students");
    XLSX.writeFile(workbook, "StudentReports.xlsx");
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // months are 0-based
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };
  const getMinDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 15);
    return date.toISOString().split("T")[0];
  };

  /**
   * Calculates today's date to set as the maximum selectable date.
   * @returns {string} The date in 'YYYY-MM-DD' format.
   */
  const getMaxDate = () => {
    return new Date().toISOString().split("T")[0];
  };

  return (
    <div className="container mx-auto p-6">
      <h2 className="text-2xl font-bold mb-4">Student Create</h2>

      <form
        onSubmit={handleSubmit}
        className="grid grid-cols-1 gap-6 mb-6 bg-white p-6 shadow rounded"
      >
        {/* Student Details Section */}
        <div>
          <h3 className="text-lg font-semibold mb-6 border-b pb-1">
            Student Information
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            <div>
              <label htmlFor="full_name" className="block font-medium mb-1">
                Student Admission Number
              </label>
              <input
                id="student_admission_number"
                type="text"
                name="student_admission_number"
                value={form.student_admission_number}
                onChange={handleChange}
                readOnly={editingIndex !== null}
                className={`border p-2 rounded w-full ${
                  editingIndex !== null ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
              />
            </div>

            <div>
              <label htmlFor="full_name" className="block font-medium mb-1">
                Full Name<span className="text-red-500">*</span>
              </label>
              <input
                id="full_name"
                type="text"
                name="full_name"
                value={form.full_name}
                onChange={handleChange}
                className="border p-2 rounded w-full"
                required
              />
            </div>
            <div>
              <label htmlFor="father_name" className="block font-medium mb-1">
                Father Name<span className="text-red-500">*</span>
              </label>
              <input
                id="father_name"
                type="text"
                name="father_name"
                value={form.father_name}
                onChange={handleChange}
                className="border p-2 rounded w-full"
                required
              />
            </div>

            <div>
              <label
                htmlFor="mobile_number"
                className="block font-medium mb-1 "
              >
                Mobile Number<span className="text-red-500">*</span>
              </label>
              <input
                id="mobile_number"
                type="text"
                name="mobile_number"
                value={form.mobile_number}
                onChange={handleChange}
                readOnly={editingIndex !== null}
                className={`border p-2 rounded w-full ${
                  editingIndex !== null ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
                maxLength={10}
              />
            </div>
            <div>
              <label htmlFor="course" className="block font-medium mb-1">
                Course<span className="text-red-500">*</span>
              </label>
              <select
                id="course"
                name="course"
                value={form.course}
                onChange={handleChange}
                className="border p-2 rounded w-full"
                required
              >
                <option value="">Select a course</option>
                <option value="CSE">CSE</option>
                <option value="AI & ML">AI & ML</option>
                <option value="Aeronautical Engineering">
                  Aeronautical Engineering
                </option>
                <option value="ECE">ECE</option>
                <option value="BBA LLB">BBA LLB</option>
                <option value="BA LLB">BA LLB</option>
                <option value="BBA">BBA</option>
                <option value="BSc Food Science & Tech">
                  BSc Food Science & Tech
                </option>
              </select>
            </div>

            <div>
              <label htmlFor="year" className="block font-medium mb-1">
                Year<span className="text-red-500">*</span>
              </label>
              <select
                id="year"
                name="year"
                value={form.year}
                onChange={handleChange}
                className="border p-2 rounded w-full"
                required
              >
                <option value="">Select a Year</option>
                <option value="I">1</option>
                <option value="II">2</option>
                <option value="III">3</option>
                <option value="IV">4</option>
              </select>
            </div>
            <div>
              <label htmlFor="semester" className="block font-medium mb-1">
                Semester<span className="text-red-500">*</span>
              </label>
              <select
                id="semester"
                name="semester"
                value={form.semester}
                onChange={handleChange}
                className="border p-2 rounded w-full"
                required
              >
                <option value="">Select a Semester</option>
                <option value="I">1</option>
                <option value="II">2</option>
              </select>
            </div>
            <div>
              <label htmlFor="email_id" className="block font-medium mb-1">
                Email
              </label>
              <input
                id="email_id"
                type="email"
                name="email_id"
                value={form.email_id}
                onChange={handleChange}
                className="border p-2 rounded w-full"
              />
            </div>
          </div>
        </div>

        {/* Transaction Details Section */}
        <div>
          <h3 className="text-lg font-semibold mb-6 border-b pb-1">
            Transaction Information
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            <div>
              <label htmlFor="fee" className="block font-medium mb-1">
                Fee<span className="text-red-500">*</span>
              </label>
              <input
                id="fee"
                type="number"
                name="fee"
                value={form.fee}
                onChange={handleChange}
                className="border p-2 rounded w-full"
                required
                min={0}
              />
            </div>

            <div>
              <label htmlFor="total_amount" className="block font-medium mb-1">
                Total Amount
              </label>
              <input
                id="total_amount"
                type="number"
                name="total_amount"
                value={form.total_amount}
                className="border p-2 rounded w-full bg-yellow-100 text-red-700 font-bold"
                disabled
              />
            </div>

            <div>
              <label
                htmlFor="payment_method"
                className="block font-medium mb-1"
              >
                Payment Method<span className="text-red-500">*</span>
              </label>
              <select
                id="payment_method"
                name="payment_method"
                value={form.payment_method}
                onChange={handleChange}
                className="border p-2 rounded w-full"
                required
              >
                <option value="">Select Payment Method</option>
                <option value="Cash">Cash</option>
                <option value="Card">Card</option>
                <option value="UPI">UPI</option>
                <option value="Bank Transfer">Bank Transfer</option>
              </select>
            </div>

            <div>
              <label className="block mb-1 font-medium">
                Payment Date<span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="payment_date"
                value={form.payment_date}
                onChange={(e) => {
                  const selectedDate = new Date(e.target.value);
                  const today = new Date();

                  selectedDate.setMinutes(
                    selectedDate.getMinutes() + selectedDate.getTimezoneOffset()
                  );
                  today.setHours(0, 0, 0, 0);

                  const minDate = new Date();
                  minDate.setDate(minDate.getDate() - 15);
                  minDate.setHours(0, 0, 0, 0);

                  if (selectedDate > today || selectedDate < minDate) {
                    toast.error(
                      "Payment date must be today or within the last 15 days."
                    );
                    return;
                  }
                  handleChange(e);
                }}
                className="border p-2 rounded w-full"
                required
                min={getMinDate()}
                max={getMaxDate()}
              />
            </div>

            <div>
              <label
                htmlFor="transaction_id"
                className="block font-medium mb-1"
              >
                Transaction ID
              </label>
              <input
                id="transaction_id"
                type="text"
                name="transaction_id"
                value={form.transaction_id}
                onChange={handleChange}
                className="border p-2 rounded w-full"
              />
            </div>

            {/* **** START: Remarks Field Added **** */}
            <div className="md:col-span-3">
              <label htmlFor="remarks" className="block font-medium mb-1">
                Remarks
              </label>
              <textarea
                id="remarks"
                name="remarks"
                value={form.remarks}
                onChange={handleChange}
                className="border p-2 rounded w-full min-h-[60px]"
                placeholder="Enter any additional notes here..."
              />
            </div>
            {/* **** END: Remarks Field Added **** */}
          </div>
        </div>

        {/* Submit Button */}
        <div className="text-right">
          <button
            type="submit"
            className="bg-blue-600 text-white py-2 px-6 rounded hover:bg-blue-700 transition"
          >
            {editingIndex !== null ? "Update Student" : "Submit"}
          </button>
        </div>
      </form>

      <h3 className="text-2xl font-semibold mb-4">Student Records</h3>

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-4 w-full">
          <div className="w-full sm:w-96">
            <input
              type="text"
              placeholder="Search by Student Admission Number or Mobile Number"
              className="border p-2 rounded w-full"
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <button
          onClick={handleDownloadExcel}
          className="bg-emerald-600 hover:bg-emerald-700 w-48 text-white py-2 px-4 rounded flex justify-center items-center gap-1"
        >
          <FaFileExcel /> Download Excel
        </button>
      </div>

      <div className="overflow-x-auto">
        <div className="h-[600px] overflow-y-auto">
          <table className="min-w-full bg-white shadow rounded text-sm border">
            <thead className="bg-gray-100 text-left">
              <tr>
                {[
                  "S NO",
                  "Transaction ID",
                  "Invoice Number",
                  "Admission Number",
                  "Year & Semester",
                  "Full Name",
                  "Father Name",
                  "Mobile Number",
                  "Email",
                  "Course",
                  "Fee",
                  "Total Amount",
                  "Payment Method",
                  "Payment Date",
                  "Remarks", // Added Remarks Column Header
                  "Action",
                  "Invoice",
                ].map((head, idx) => (
                  <th
                    key={idx}
                    className="px-4 py-2 border-b whitespace-nowrap"
                  >
                    {head}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredStudents.length === 0 ? (
                <tr>
                  <td colSpan="17" className="text-center py-4 text-gray-500">
                    No students found.
                  </td>
                </tr>
              ) : (
                currentStudents.map((student, index) => (
                  <tr
                    key={student.student_id || index}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-4 py-2 border-b">
                      {indexOfFirstStudent + index + 1}
                    </td>
                    <td className="px-4 py-2 border-b max-w-[150px] truncate">
                      {student.transaction_id || "N/A"}
                    </td>
                    <td className="px-4 py-2 border-b max-w-[150px] truncate">
                      {student.invoice_number}
                    </td>
                    <td className="px-4 py-2 border-b max-w-[150px] truncate">
                      {student.student_admission_number || "N/A"}
                    </td>
                    <td className="px-4 py-2 border-b max-w-[150px] truncate">
                      {student.year} - {student.semester}sem
                    </td>
                    <td className="px-4 py-2 border-b max-w-[180px] truncate">
                      {student.full_name}
                    </td>
                    <td className="px-4 py-2 border-b max-w-[180px] truncate">
                      {student.father_name}
                    </td>
                    <td className="px-4 py-2 border-b max-w-[120px] truncate">
                      {student.mobile_number}
                    </td>
                    <td className="px-4 py-2 border-b max-w-[200px] truncate">
                      {student.email_id}
                    </td>
                    <td className="px-4 py-2 border-b max-w-[150px] truncate">
                      {student.course}
                    </td>
                    <td className="px-4 py-2 border-b">
                      {displayINRCurrency(student.fee)}
                    </td>
                    <td className="px-4 py-2 border-b">
                      {displayINRCurrency(student.total_amount)}
                    </td>
                    <td className="px-4 py-2 border-b">
                      {student.payment_method}
                    </td>
                    <td className="px-4 py-2 border-b">
                      {student.payment_date
                        ? formatDate(student.payment_date)
                        : "N/A"}
                    </td>
                    {/* **** START: Remarks Data Cell Added **** */}
                    <td className="px-4 py-2 border-b max-w-[200px] truncate">
                      {student.remarks || "N/A"}
                    </td>
                    {/* **** END: Remarks Data Cell Added **** */}
                    <td className="px-4 py-2 border-b">
                      <button
                        onClick={() => handleEdit(indexOfFirstStudent + index)}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm w-full sm:w-auto"
                      >
                        Edit
                      </button>
                    </td>
                    <td className="px-4 py-2 border-b">
                      <button
                        onClick={() => handleInvoice(student)}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-1 rounded-md text-sm"
                      >
                        Invoice
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="relative">
          {totalPages > 1 ? (
            <div className="flex justify-center items-center gap-2 flex-wrap relative">
              <button
                className="px-3 py-1 mt-2 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>

              <div className=" top-full mt-2 text-base font-semibold">
                Page {currentPage} of {totalPages}
              </div>
              <button
                className="px-3 py-1 mt-2 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
              >
                Next
              </button>

              <div className="absolute right-20 text-base font-semibold">
                Total Records:{" "}
                <span className="ml-1">{filteredStudents.length}</span>
              </div>
            </div>
          ) : (
            <div className="flex justify-end text-base font-semibold">
              Total Records:{" "}
              <span className="ml-1">{filteredStudents.length}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentReports;